import { normalizeOffsetForForm } from './designForm.utils'
import { SEASONAL_RECOMMENDATION_INTENT } from './seasonalRecommendation.constants'

const padDatePart = (value) => String(value).padStart(2, '0')

const annualDate = ({ year, month, day, isLastDay = false }) => {
  const resolvedDay = isLastDay ? new Date(year, month, 0).getDate() : day

  return `${year}-${padDatePart(month)}-${padDatePart(resolvedDay)}`
}

const annualRangeEndYear = ({ year, startMonth, startDay, endMonth, endDay, endIsLastDay }) => {
  const crossesYear =
    endMonth < startMonth || (endMonth === startMonth && !endIsLastDay && endDay < startDay)

  return year + (crossesYear ? 1 : 0)
}

const CLEARED_SCHEDULE_TIMING = Object.freeze({
  is_yearly: false,
  start_type: null,
  starts_on: null,
  start_occasion_id: null,
  start_holiday: null,
  start_offset_days: null,
  end_type: null,
  ends_on: null,
  end_occasion_id: null,
  end_holiday: null,
  end_offset_days: null,
  end_days_after_start: null,
  end_matches_start_holiday: false,
})

export function resolveSeasonalPlanningYear(value) {
  const year = Number(value)

  return Number.isInteger(year) ? year : new Date().getFullYear()
}

const resolveSeasonalTag = ({ seasonalTagId, seasonalTags, scheduleProfile }) => {
  const profileSeasonalTag =
    Number(scheduleProfile?.seasonal_tag_id) === Number(seasonalTagId)
      ? scheduleProfile?.seasonal_tag
      : null

  return (
    seasonalTags.find((candidate) => Number(candidate.id) === Number(seasonalTagId)) ??
    profileSeasonalTag
  )
}

const isPublishedInteger = (value) => value != null && Number.isInteger(Number(value))

const publishedProfileTiming = (scheduleProfile) => {
  const hasPublishedOffsets =
    scheduleProfile?.published_start_offset_days != null &&
    scheduleProfile?.published_end_offset_days != null &&
    Number.isFinite(Number(scheduleProfile.published_start_offset_days)) &&
    Number.isFinite(Number(scheduleProfile.published_end_offset_days))
  const hasPublishedAnnualRange =
    isPublishedInteger(scheduleProfile?.published_start_month) &&
    isPublishedInteger(scheduleProfile?.published_start_day) &&
    isPublishedInteger(scheduleProfile?.published_end_month) &&
    (scheduleProfile?.published_end_is_last_day ||
      isPublishedInteger(scheduleProfile?.published_end_day))
  const mode =
    scheduleProfile?.published_timing_mode ??
    (hasPublishedOffsets ? 'occasion_offset' : hasPublishedAnnualRange ? 'annual_range' : null)
  const hasOccasionOffset = mode === 'occasion_offset' && hasPublishedOffsets
  const hasAnnualRange = mode === 'annual_range' && hasPublishedAnnualRange

  return { hasAnnualRange, hasOccasionOffset }
}

export function buildSeasonalScheduleRecommendationTransition({
  schedule,
  seasonalTagId,
  seasonalTags = [],
  scheduleProfile = null,
  intent,
  planningYear,
}) {
  const nextSchedule = { ...schedule }
  const seasonalTag = resolveSeasonalTag({ seasonalTagId, seasonalTags, scheduleProfile })
  const isInitialSetup = intent === SEASONAL_RECOMMENDATION_INTENT.INITIAL_SETUP
  const isTimingOnly = intent === SEASONAL_RECOMMENDATION_INTENT.USE_DEFAULT_TIMING
  const isSeasonalTagChange = intent === SEASONAL_RECOMMENDATION_INTENT.SEASONAL_TAG_CHANGE
  const preserveCustomTiming =
    isSeasonalTagChange &&
    schedule?.timing_mode === 'custom' &&
    ['holiday_offset', 'fixed_date'].includes(schedule?.start_type)
  const shouldConfigure =
    isInitialSetup ||
    isTimingOnly ||
    (isSeasonalTagChange && (schedule?.timing_mode === 'automatic' || preserveCustomTiming))
  const { hasAnnualRange, hasOccasionOffset } = publishedProfileTiming(scheduleProfile)
  const automaticTimingAvailable =
    Number(scheduleProfile?.id) > 0 &&
    (hasAnnualRange || (hasOccasionOffset && Boolean(seasonalTag)))

  if (!shouldConfigure || (!seasonalTag && !hasAnnualRange)) {
    return {
      automaticTimingAvailable,
      didConfigure: false,
      schedule: nextSchedule,
      seasonalTag,
    }
  }

  const isDateBackedOccasion = Boolean(
    seasonalTag && seasonalTag.tag_type !== 'season' && seasonalTag.kind === 'date_backed'
  )
  const hasCompatibleCustomTiming =
    preserveCustomTiming && (isDateBackedOccasion || nextSchedule.start_type === 'fixed_date')

  if (hasCompatibleCustomTiming) {
    Object.assign(nextSchedule, {
      seasonal_schedule_profile_id: null,
      timing_mode: 'custom',
    })
    if (isDateBackedOccasion && nextSchedule.start_type === 'holiday_offset') {
      Object.assign(nextSchedule, {
        start_occasion_id: seasonalTag.id,
        start_holiday: seasonalTag.key,
      })
      if (nextSchedule.end_type === 'holiday_offset') {
        Object.assign(nextSchedule, {
          end_occasion_id: seasonalTag.id,
          end_holiday: seasonalTag.key,
        })
      }
    }
  } else if (hasOccasionOffset) {
    Object.assign(nextSchedule, {
      ...CLEARED_SCHEDULE_TIMING,
      ...(isInitialSetup ? { is_enabled: true } : {}),
      seasonal_schedule_profile_id: scheduleProfile.id,
      timing_mode: 'automatic',
      start_type: 'holiday_offset',
      start_occasion_id: seasonalTag.id,
      start_holiday: seasonalTag.key,
      start_offset_days: normalizeOffsetForForm(scheduleProfile.published_start_offset_days, 0),
      end_type: 'holiday_offset',
      end_occasion_id: seasonalTag.id,
      end_holiday: seasonalTag.key,
      end_offset_days: normalizeOffsetForForm(scheduleProfile.published_end_offset_days, 0),
      end_matches_start_holiday: true,
    })
  } else if (hasAnnualRange) {
    const startMonth = Number(scheduleProfile.published_start_month)
    const startDay = Number(scheduleProfile.published_start_day)
    const endMonth = Number(scheduleProfile.published_end_month)
    const endDay = Number(scheduleProfile.published_end_day)
    const endIsLastDay = Boolean(scheduleProfile.published_end_is_last_day)
    const endYear = annualRangeEndYear({
      year: planningYear,
      startMonth,
      startDay,
      endMonth,
      endDay,
      endIsLastDay,
    })
    Object.assign(nextSchedule, {
      ...CLEARED_SCHEDULE_TIMING,
      ...(isInitialSetup ? { is_enabled: true } : {}),
      seasonal_schedule_profile_id: scheduleProfile.id,
      timing_mode: 'automatic',
      is_yearly: true,
      start_type: 'fixed_date',
      starts_on: annualDate({ year: planningYear, month: startMonth, day: startDay }),
      end_type: 'fixed_date',
      ends_on: annualDate({
        year: endYear,
        month: endMonth,
        day: endDay,
        isLastDay: endIsLastDay,
      }),
    })
  } else {
    Object.assign(nextSchedule, {
      ...CLEARED_SCHEDULE_TIMING,
      is_enabled: false,
      timing_mode: 'custom',
      seasonal_schedule_profile_id: null,
      ...(isDateBackedOccasion
        ? {
            start_type: 'holiday_offset',
            start_occasion_id: seasonalTag.id,
            start_holiday: seasonalTag.key,
            start_offset_days: 0,
            end_type: 'holiday_offset',
            end_occasion_id: seasonalTag.id,
            end_holiday: seasonalTag.key,
            end_offset_days: 0,
            end_matches_start_holiday: true,
          }
        : {}),
    })
  }

  return {
    automaticTimingAvailable,
    didConfigure: true,
    schedule: nextSchedule,
    seasonalTag,
  }
}
